import React, { useState } from 'react';
import { PageHero } from '../components/PageHero';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Mail, Check } from 'lucide-react';
import { submitToNewsletter } from '../lib/newsletter';
export function Newsletter() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    const success = await submitToNewsletter(email, name);
    if (success) {
      setStatus('success');
      setEmail('');
      setName('');
    } else {
      setStatus('error');
    }
  };
  return <div className="animate-fade-in-up" data-id="element-315">
      <PageHero title="Thoughts on arriving at yourself" subtitle="Weekly (ish) reflections on anxiety, self-acceptance, and what it really means to build a life that feels like yours." data-id="element-316" />

      <section className="py-20 bg-white" data-id="element-317">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-318">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-319">
            {/* Left Column: Content */}
            <div className="space-y-12" data-id="element-320">
              <div data-id="element-321">
                <h2 className="text-2xl font-medium text-teal mb-6" data-id="element-322">
                  What This Newsletter Is
                </h2>
                <p className="text-lg text-teal/80 mb-6" data-id="element-323">
                  This isn't another productivity hack newsletter. It's not
                  about "crushing your goals" or "living your best life" or any
                  of that noise.
                </p>
                <p className="text-lg text-teal/80 mb-4" data-id="element-324">
                  It's honest thoughts on:
                </p>
                <ul className="space-y-3 pl-4 border-l-2 border-terracotta" data-id="element-325">
                  {['Managing anxiety without trying to eliminate it', 'Breaking free from people-pleasing', 'What acceptance actually means', 'Navigating your 20s and 30s', 'Building a life that feels authentic'].map((item, i) => <li key={i} className="text-teal/80" data-id="element-326">
                      {item}
                    </li>)}
                </ul>
              </div>

              <div data-id="element-327">
                <h2 className="text-2xl font-medium text-teal mb-6" data-id="element-328">
                  What You'll Get
                </h2>
                <p className="text-teal/80 mb-6" data-id="element-329">
                  Each week (ish, I'm human), you'll get an email that feels
                  like sitting down with a therapist friend who gets it.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-330">
                  {['Book & podcast recs', 'Behind-the-scenes thoughts', 'Rants on toxic positivity', 'Therapy tools & frameworks'].map((item, i) => <div key={i} className="flex items-center p-4 bg-neutral-soft rounded-lg" data-id="element-331">
                      <Check className="w-4 h-4 text-sage mr-3" data-id="element-332" />
                      <span className="text-sm text-teal font-medium" data-id="element-333">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Right Column: Signup Form */}
            <div className="lg:sticky lg:top-32 h-fit" data-id="element-334">
              <div className="bg-teal p-8 md:p-10 rounded-3xl shadow-xl text-white relative overflow-hidden" data-id="element-335">
                <div className="absolute top-0 right-0 w-64 h-64 bg-sage/20 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" data-id="element-336" />

                <div className="relative z-10" data-id="element-337">
                  <h3 className="text-2xl font-medium mb-2" data-id="element-338">Join the list</h3>
                  <p className="text-white/80 mb-8" data-id="element-339">
                    Free. Weekly (ish). Unsubscribe anytime.
                  </p>

                  {status === 'success' ? <div className="text-center py-12 bg-white/10 rounded-xl animate-fade-in-up" data-id="element-340">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 text-teal" data-id="element-341">
                        <Mail className="w-8 h-8" data-id="element-342" />
                      </div>
                      <h4 className="text-xl font-medium mb-2" data-id="element-343">You're in!</h4>
                      <p className="text-white/80" data-id="element-344">
                        Check your inbox for a welcome note.
                      </p>
                      <Button variant="ghost" className="mt-6 text-white hover:bg-white/10" onClick={() => setStatus('idle')} data-id="element-345">
                        Reset form
                      </Button>
                    </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-346">
                      <Input variant="dark" label="First Name (Optional)" placeholder="Jess" value={name} onChange={e => setName(e.target.value)} data-id="element-347" />
                      <Input variant="dark" type="email" label="Email Address" placeholder="hello@example.com" required value={email} onChange={e => setEmail(e.target.value)} data-id="element-348" />
                      <Button type="submit" className="w-full bg-terracotta hover:bg-terracotta-hover text-white border-none mt-4" isLoading={status === 'loading'} data-id="element-349">
                        Send me the newsletter
                      </Button>
                      {status === 'error' && <p className="text-xs text-red-200 text-center" data-id="element-350">
                          Something went wrong. Please try again.
                        </p>}
                      <p className="text-xs text-white/60 text-center mt-4" data-id="element-351">
                        I'll never spam you or share your email.
                      </p>
                    </form>}
                </div>
              </div>

              <div className="mt-8 bg-neutral-soft p-6 rounded-2xl border border-neutral-200" data-id="element-352">
                <h4 className="font-medium text-teal mb-2" data-id="element-353">What this isn't:</h4>
                <ul className="space-y-2 text-sm text-teal/70" data-id="element-354">
                  <li className="flex items-start" data-id="element-355">
                    <span className="mr-2" data-id="element-356">·</span> Medical advice
                  </li>
                  <li className="flex items-start" data-id="element-357">
                    <span className="mr-2" data-id="element-358">·</span> Toxic positivity
                  </li>
                  <li className="flex items-start" data-id="element-359">
                    <span className="mr-2" data-id="element-360">·</span> A sales funnel
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}